// @ts-nocheck
import { ServiceResult } from "@insite/client-framework/Services/ApiService";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { MfaChallengeResult, MfaVerifyResult } from "@store/Framework/Services/MfaService";
import siteMessage from "@insite/client-framework/SiteMessage";
import translate from "@insite/client-framework/Translate";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import Modal, { ModalPresentationProps } from "@insite/mobius/Modal";
import TextField, { TextFieldProps } from "@insite/mobius/TextField";
import Typography, { TypographyProps } from "@insite/mobius/Typography/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React, { useEffect, useRef, useState } from "react";
import { css } from "styled-components";

export interface MfaOtpModalStyles {
    modal?: ModalPresentationProps;
    instructionsText?: TypographyProps;
    codeTextField?: TextFieldProps;
    countdownText?: TypographyProps;
    errorText?: TypographyProps;
    actionsWrapper?: InjectableCss;
    validateButton?: ButtonPresentationProps;
    resendButton?: ButtonPresentationProps;
}

export const mfaOtpModalStyles: MfaOtpModalStyles = {
    modal: {
        sizeVariant: "small",
    },
    countdownText: {
        css: css`
            display: block;
            margin-top: 8px;
            font-size: ${x => x.theme.design.typography.size.smaller};
        `,
    },
    errorText: {
        color: "danger",
        css: css`
            display: block;
            margin-top: 8px;
        `,
    },
    actionsWrapper: {
        css: css`
            display: flex;
            justify-content: space-between;
            align-items: center;
            margin-top: 16px;
            gap: 12px;
        `,
    },
};

const styles = mfaOtpModalStyles;

export interface MfaOtpModalProps {
    isOpen: boolean;
    maskedEmail: string;
    codeLength: number;
    expiresInSeconds: number;
    /** Verifies the entered code against the current challenge. */
    onVerify: (code: string) => Promise<ServiceResult<MfaVerifyResult>>;
    /** Re-issues a challenge code (resend); returns the new challenge details. */
    onResend: () => Promise<ServiceResult<MfaChallengeResult>>;
    /** Called with the single-use ticket after successful verification. */
    onSuccess: (ticketId: string) => void;
    onClose: () => void;
}

const formatTime = (totalSeconds: number) => {
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;
    return `${minutes}:${seconds.toString().padStart(2, "0")}`;
};

const MfaOtpModal = ({
    isOpen,
    maskedEmail,
    codeLength,
    expiresInSeconds,
    onVerify,
    onResend,
    onSuccess,
    onClose,
}: MfaOtpModalProps) => {
    const [code, setCode] = useState("");
    const [errorMessage, setErrorMessage] = useState("");
    const [isBusy, setIsBusy] = useState(false);
    const [maxAttemptsReached, setMaxAttemptsReached] = useState(false);
    const [secondsRemaining, setSecondsRemaining] = useState(expiresInSeconds);
    const timerRef = useRef<ReturnType<typeof setInterval>>(undefined);

    // Reset transient state whenever the modal is (re)opened or the expiry changes (e.g. after resend).
    useEffect(() => {
        if (isOpen) {
            setCode("");
            setErrorMessage("");
            setMaxAttemptsReached(false);
            setSecondsRemaining(expiresInSeconds);
        }
    }, [isOpen, expiresInSeconds]);

    useEffect(() => {
        if (!isOpen) {
            return undefined;
        }

        timerRef.current = setInterval(() => {
            setSecondsRemaining(prev => (prev > 0 ? prev - 1 : 0));
        }, 1000);

        return () => {
            if (timerRef.current) {
                clearInterval(timerRef.current);
            }
        };
    }, [isOpen]);

    const isExpired = secondsRemaining <= 0;

    const handleVerify = async () => {
        if (!code || code.length < 1 || isBusy) {
            return;
        }

        setIsBusy(true);
        setErrorMessage("");

        try {
            const result = await onVerify(code);

            if (result.successful && result.result.verified) {
                onSuccess(result.result.ticketId);
                return;
            }

            setCode("");

            // A successful response with no remaining attempts means the challenge is now locked; show a
            // dedicated state and prompt the user to resend instead of a generic invalid-code error.
            if (result.successful && result.result.attemptsRemaining <= 0) {
                setMaxAttemptsReached(true);
                setErrorMessage("");
                return;
            }

            setErrorMessage(
                (!result.successful && result.errorMessage) ||
                    translate("The code you entered is incorrect. Please try again."),
            );
        } catch {
            setCode("");
            setErrorMessage(translate("The code you entered is incorrect. Please try again."));
        } finally {
            setIsBusy(false);
        }
    };

    const handleResend = async () => {
        if (isBusy) {
            return;
        }

        setIsBusy(true);
        setErrorMessage("");

        try {
            const result = await onResend();

            setCode("");

            if (result.successful) {
                setMaxAttemptsReached(false);
                setSecondsRemaining(result.result.expiresInSeconds);
            } else {
                setErrorMessage(
                    result.errorMessage || translate("We couldn't resend the code. Please try again."),
                );
            }
        } catch {
            setErrorMessage(translate("We couldn't resend the code. Please try again."));
        } finally {
            setIsBusy(false);
        }
    };

    const onCodeChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const digitsOnly = event.currentTarget.value.replace(/\D/g, "").slice(0, codeLength);
        setCode(digitsOnly);
    };

    const submitOnEnter = (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        // The modal is rendered (via the React tree) inside the outer sign-in form; without this the
        // submit event bubbles up and re-triggers sign-in, which would re-issue a new challenge code.
        event.stopPropagation();
        handleVerify();
    };

    return (
        <Modal
            headline={translate("Email Verification")}
            {...styles.modal}
            isOpen={isOpen}
            handleClose={onClose}
            closeOnScrimClick={false}
            data-test-selector="mfaOtpModal"
        >
            <form onSubmit={submitOnEnter} noValidate>
                <Typography {...styles.instructionsText}>
                    {siteMessage("Mfa_Instructions", maskedEmail)}
                </Typography>
                <TextField
                    {...styles.codeTextField}
                    label={translate("Verification Code")}
                    value={code}
                    onChange={onCodeChange}
                    error={errorMessage}
                    inputMode="numeric"
                    autoComplete="one-time-code"
                    maxLength={codeLength}
                    disabled={isBusy || maxAttemptsReached}
                    data-test-selector="mfaOtp_code"
                />
                {maxAttemptsReached ? (
                    <Typography {...styles.errorText} data-test-selector="mfaOtp_maxAttempts">
                        {translate("Too many failed attempts. Please request a new code to try again.")}
                    </Typography>
                ) : isExpired ? (
                    <Typography {...styles.countdownText}>{siteMessage("Mfa_CodeExpired")}</Typography>
                ) : (
                    <Typography {...styles.countdownText}>
                        {siteMessage("Mfa_CodeExpiresIn", formatTime(secondsRemaining))}
                    </Typography>
                )}
                <StyledWrapper {...styles.actionsWrapper}>
                    <Button
                        {...styles.resendButton}
                        variant="secondary"
                        type="button"
                        disabled={isBusy}
                        onClick={handleResend}
                        data-test-selector="mfaOtp_resend"
                    >
                        {translate("Resend Code")}
                    </Button>
                    <Button
                        {...styles.validateButton}
                        type="submit"
                        disabled={isBusy || isExpired || maxAttemptsReached || code.length === 0}
                        data-test-selector="mfaOtp_validate"
                    >
                        {translate("Validate Email")}
                    </Button>
                </StyledWrapper>
            </form>
        </Modal>
    );
};

export default MfaOtpModal;
